import React, { useContext, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spin, Typography } from 'antd';
import { AxiosError } from 'axios';
import { AuthContext, AuthContextState } from '../../context/AuthContext';
import MainLayout from '../../components/MainLayout';
import { DASHBOARD_ROUTE, GITHUB_OAUTH_STATE_LOCALSTORAGE_KEY, LOGIN_ROUTE } from '../../config/constants';
import styles from './GitHubOAuthCallback.module.css';

export interface GitHubOAuthCallbackProps {};

// receives redirect with authorization code from github
// and sends authorization code to backend to perform oauth flow
const GitHubOAuthCallback = (props: GitHubOAuthCallbackProps): JSX.Element | null => {
  // ref to avoid authenticating GitHub multiple times on multiple renders
  const authenticateGitHubCalled = useRef<boolean>(false);
  // get github authenticate function from auth context
  const { authenticateGitHubUser, checkAuthenticated, fetchMe } = useContext(AuthContext) as AuthContextState;
  // get url search params
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // set document title
  useEffect(() => {
    document.title = 'GitHub OAuth | Sigmoid Academy';
  }, []);

  // authenticate GitHub call
  useEffect(() => {
    if (authenticateGitHubCalled.current) {
      return;
    }

    // get authorization code parameter
    const code = searchParams.get('code');
    // get received state parameter
    const receivedState = searchParams.get('state');
    // retrieve saved state
    const storedState = localStorage.getItem(GITHUB_OAUTH_STATE_LOCALSTORAGE_KEY);

    if (!code || !receivedState || !storedState || receivedState !== storedState) {
      navigate(
        LOGIN_ROUTE,
        {
          state: {
            statusMessages: [
              {
                message: 'GitHub OAuth failed',
                status: 'error'
              }
            ]
          }
        }
      );

      return;
    }

    authenticateGitHubCalled.current = true;

    // send authenticate github request
    authenticateGitHubUser(
      code
    ).then(checkAuthenticated
    ).then(fetchMe
    ).then(() => navigate(DASHBOARD_ROUTE)
    ).catch((error: AxiosError) => navigate(
        LOGIN_ROUTE,
        {
          state: {
            statusMessages: [
              {
                message: 'GitHub OAuth failed',
                status: 'error'
              }
            ]
          }
        }
      )
    );
  }, [searchParams, navigate, authenticateGitHubUser, checkAuthenticated, fetchMe]);

  return (
    <div>
      <MainLayout selectedKey='login'>
        <div className={styles.mainContainer}>
          <Spin
            tip={
              <Typography.Text
                strong={true}
                className={styles.mainTip}
              >
                Authenticating GitHub Account
              </Typography.Text>
            }
            size='large'
          />
        </div>
      </MainLayout>
    </div>
  );
};

export default GitHubOAuthCallback;
